import { Platform } from 'react-native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

import { isAmericanBarrierHit } from '../utils/barrierStyle';
const PRODUCT_LINE_COLOR = '#E53935';
const UNDERLYING_COLORS = ['#00C853', '#1976D2', '#00ACC1'];

interface UnderlyingPdfData {
  name: string;
  isin: string;
  initialFixingLevel: number;
  strikeLevel: number;
  barrierLevel: number;
  marketPrice: number;
  performance: number;
  barrierDistance: number;
  strikeDistance: number;
  autocallDistance: number | null;
  couponBarrierDistance: number | null;
  underlyingCurrency?: string;
  upperStrikePct: number | null;
}

interface CouponPdfData {
  periodNumber: number;
  exDate: string;
  paymentDate: string;
  amount: string;
  isPaid: boolean;
  isAutocalled: boolean;
}

interface EarlyRedemptionPdfData {
  observationDate: string;
  redemptionLevel?: number;
}

interface HistoryPoint {
  date: string;
  price: number;
}

interface UnderlyingHistorySeries {
  name: string;
  history: HistoryPoint[];
}

interface UnderlyingBaseReference {
  name: string;
  isin: string;
  initialFixingLevel: number;
}

interface ProductPdfData {
  name: string;
  isin: string | null;
  valor: string;
  currency: string;
  bid: number;
  ask: number;
  productCode?: string;
  productType: string;
  issuer: string;
  issuePrice?: number | null;
  tenorMonths?: number | null;
  denomination?: number | null;
  issueDate: string;
  redemptionDate: string;
  initialFixing: string;
  finalFixing: string;
  settlementTypeName?: string | null;
  grade?: string | null;
  recovery?: string | null;
  upside?: number | null;
  upsideParticipation?: number | null;
  downside?: number | null;
  downsideParticipation?: number | null;
  underlyings: UnderlyingPdfData[];
  coupons: CouponPdfData[];
  barrierLevel: number;
  barrierUpper?: number | null;
  barrierStyleName?: string | null;
  barrierHitRecorded?: boolean;
  strikeLevel: number;
  bonusLevel?: number | null;
  upperStrikePct?: number | null;
  premiumPct?: number | null;
  quotationType?: string | null;
  basketOptionTypeName?: string | null;
  leverage?: number | null;
  ratio?: number | null;
  stopLoss?: number | null;
  stopLossDistance?: number | null;
  couponBarrier: number | null;
  couponMemory: boolean | null;
  coupon: number | null;
  couponTrigger?: number | null;
  capitalProtectionPct?: number | null;
  couponStepUp?: number | null;
  couponFrequency: string | null;
  couponMinPct: number | null;
  couponMaxPct: number | null;
  couponType: string | null;
  referenceRate: string | null;
  tradedNotional?: number | null;
  currentNotional?: number | null;
}

interface ProductPdfSections {
  atGlance: boolean;
  barrier: boolean;
  underlyings: boolean;
  coupons: boolean;
  earlyRedemption: boolean;
  performance: boolean;
  couponDetails: boolean;
}

interface ProductPdfSectionProps {
  atGlance?: Record<string, boolean>;
  barrier?: Record<string, boolean>;
  underlyings?: Record<string, boolean>;
  coupons?: Record<string, boolean>;
  performance?: Record<string, boolean>;
  couponDetails?: Record<string, boolean>;
  earlyRedemption?: Record<string, boolean>;
}

interface ProductPdfSectionTitles {
  barrier?: string;
  couponDetails?: string;
}

export interface ExportProductPdfInput {
  product: ProductPdfData;
  sections: ProductPdfSections;
  sectionProps?: ProductPdfSectionProps;
  sectionTitles?: ProductPdfSectionTitles;
  earlyRedemption: EarlyRedemptionPdfData[];
  priceHistory: HistoryPoint[];
  underlyingsHistory: UnderlyingHistorySeries[];
  /** Company name from user profile (tbld_Adress); empty if unknown */
  exporterCompanyName?: string | null;
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
  if (!isFiniteNumber(value)) return '-';
  return value.toFixed(decimals);
};

const formatInteger = (value: number | null | undefined): string => {
  if (!isFiniteNumber(value)) return '-';
  return Math.round(value).toLocaleString('en-US');
};

const formatPct = (value: number | null | undefined, decimals: number = 2): string =>
  isFiniteNumber(value) ? `${value.toFixed(decimals)}%` : '-';

const formatPctTrimInteger = (value: number | null | undefined): string => {
  if (!isFiniteNumber(value)) return '-';
  const decimals = Math.abs(value - Math.round(value)) < 1e-9 ? 0 : 2;
  return `${value.toFixed(decimals)}%`;
};

const formatRatioPct = (value: number | null | undefined, decimals: number = 2): string =>
  isFiniteNumber(value) ? `${(value * 100).toFixed(decimals)}%` : '-';

const negativeClass = (value: number | null | undefined): string =>
  isFiniteNumber(value) && value < 0 ? 'value-negative' : '';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const toSafeText = (value: unknown): string => {
  if (value == null) return '-';
  return String(value);
};

const escapeHtml = (value: unknown): string =>
  toSafeText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatShortDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

const buildCombinedPerformanceSvg = (
  priceHistory: HistoryPoint[],
  underlyingsHistory: UnderlyingHistorySeries[],
  showUnderlyings: boolean,
  productUnderlyings: UnderlyingBaseReference[],
): string => {
  const sortedProduct = [...priceHistory].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  if (sortedProduct.length < 2) {
    return '<p class="no-data-note">Insufficient data available to plot the performance chart.</p>';
  }

  const productBase = sortedProduct[0].price > 0 ? sortedProduct[0].price : 1;
  const productSeries = sortedProduct.map((point) => (point.price / productBase) * 100);
  const series: Array<{ name: string; color: string; values: number[] }> = [
    { name: 'Product', color: PRODUCT_LINE_COLOR, values: productSeries },
  ];

  if (showUnderlyings) {
    underlyingsHistory.forEach((underlying, index) => {
      const color = UNDERLYING_COLORS[index % UNDERLYING_COLORS.length];
      const sortedUnderlying = [...(underlying.history || [])].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
      if (sortedUnderlying.length === 0) return;

      const matchingUnderlying = productUnderlyings.find(
        (item) =>
          item.name?.trim().toLowerCase() === underlying.name?.trim().toLowerCase(),
      );
      const initialFixingLevel = matchingUnderlying?.initialFixingLevel;
      const base =
        initialFixingLevel != null && Number.isFinite(initialFixingLevel) && initialFixingLevel > 0
          ? initialFixingLevel
          : sortedUnderlying[0].price > 0
            ? sortedUnderlying[0].price
            : 1;
      const values = sortedProduct.map((productPoint) => {
        const productDate = new Date(productPoint.date).getTime();
        let matchPrice: number | null = null;
        for (let i = sortedUnderlying.length - 1; i >= 0; i--) {
          const underlyingDate = new Date(sortedUnderlying[i].date).getTime();
          if (underlyingDate <= productDate) {
            matchPrice = sortedUnderlying[i].price;
            break;
          }
        }
        if (matchPrice == null) return 100;
        return (matchPrice / base) * 100;
      });
      series.push({ name: underlying.name || `Underlying ${index + 1}`, color, values });
    });
  }

  const allValues = series.flatMap((item) => item.values).filter((value) => Number.isFinite(value));
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min || 1;

  const width = 700;
  const height = 260;
  const left = 52;
  const right = 14;
  const top = 14;
  const bottom = 38;
  const graphWidth = width - left - right;
  const graphHeight = height - top - bottom;

  const xAt = (index: number): number =>
    left + (index * graphWidth) / Math.max(1, sortedProduct.length - 1);
  const yAt = (value: number): number => top + ((max - value) / range) * graphHeight;

  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const value = max - (i * range) / 4;
    const y = top + (i * graphHeight) / 4;
    return `
      <line x1="${left}" y1="${y}" x2="${width - right}" y2="${y}" stroke="#F3F4F6" stroke-width="1" />
      <text x="${left - 8}" y="${y + 4}" text-anchor="end" fill="#6B7280" font-size="10">${value.toFixed(
      0,
    )}%</text>
    `;
  }).join('');

  const xTickCount = Math.min(6, sortedProduct.length);
  const xTicks = Array.from({ length: xTickCount }, (_, i) => {
    const pointIndex =
      xTickCount === 1 ? 0 : Math.round((i * (sortedProduct.length - 1)) / (xTickCount - 1));
    const x = xAt(pointIndex);
    return `
      <line x1="${x}" y1="${top + graphHeight}" x2="${x}" y2="${top + graphHeight + 4}" stroke="#9CA3AF" stroke-width="1" />
      <text x="${x}" y="${height - 10}" text-anchor="middle" fill="#6B7280" font-size="10">${formatShortDate(
      sortedProduct[pointIndex].date,
    )}</text>
    `;
  }).join('');

  const polylines = series
    .map((entry) => {
      const points = entry.values
        .map((value, index) => `${xAt(index)},${yAt(value)}`)
        .join(' ');
      return `<polyline fill="none" stroke="${entry.color}" stroke-width="2.2" points="${points}" />`;
    })
    .join('');

  const legend = series
    .map(
      (entry) => `
      <div class="perf-legend-item">
        <svg class="perf-legend-marker" viewBox="0 0 20 12" aria-hidden="true">
          <line x1="1" y1="6" x2="19" y2="6" stroke="${entry.color}" stroke-width="2.8" stroke-linecap="round" />
          <circle cx="10" cy="6" r="2.4" fill="${entry.color}" stroke="#FFFFFF" stroke-width="0.8" />
        </svg>
        <span>${escapeHtml(entry.name)}</span>
      </div>
    `,
    )
    .join('');

  return `
    <div class="perf-card">
      <svg viewBox="0 0 ${width} ${height}" class="perf-chart">
        ${yTicks}
        <line x1="${left}" y1="${top + graphHeight}" x2="${width - right}" y2="${top + graphHeight}" stroke="#D1D5DB" stroke-width="1" />
        <line x1="${left}" y1="${top}" x2="${left}" y2="${top + graphHeight}" stroke="#D1D5DB" stroke-width="1" />
        ${xTicks}
        ${polylines}
      </svg>
      <div class="perf-legend">${legend}</div>
    </div>
  `;
};

const buildHtml = ({
  product,
  sections,
  sectionProps,
  sectionTitles,
  earlyRedemption,
  priceHistory,
  underlyingsHistory,
  exporterCompanyName,
}: ExportProductPdfInput,
  documentTitle: string,
  pdfOptions?: { webRaster?: boolean },
): string => {
  const exporterLabel = (exporterCompanyName ?? '').trim();
  const eyebrowText = exporterLabel
    ? `${exporterLabel} - Product detail export`
    : 'Product detail export';
  const atGlanceProps = sectionProps?.atGlance || {};
  const underlyingsText = product.underlyings?.length
    ? product.underlyings.map((u) => u.name).filter(Boolean).join(', ')
    : '-';
  const atGlanceRows: Array<[string, string, string?]> = [];
  const pushAtGlanceRow = (
    label: string,
    value: string,
    propKey?: string,
    defaultVisible: boolean = true,
  ) => {
    const isVisible = propKey ? atGlanceProps[propKey] ?? defaultVisible : defaultVisible;
    if (isVisible) {
      atGlanceRows.push([label, value]);
    }
  };

  const productTypeDisplay = [product.productCode, product.productType].filter(Boolean).join(' – ');
  pushAtGlanceRow('Product type', productTypeDisplay || '-', undefined, true);
  pushAtGlanceRow('Issuer', product.issuer || '-', 'Issuer', true);
  pushAtGlanceRow('Underlyings', underlyingsText, undefined, true);
  pushAtGlanceRow('Issue price', formatPct(product.issuePrice), 'issuePrice', true);
  pushAtGlanceRow(
    'Tenor',
    product.tenorMonths != null ? `${formatInteger(product.tenorMonths)} months` : '-',
    undefined,
    true,
  );
  pushAtGlanceRow(
    'Coupon',
    product.coupon != null ? `${formatNumber(product.coupon)}%` : '-',
    'coupon',
    true,
  );
  pushAtGlanceRow(
    'Denomination',
    product.denomination != null ? `${product.currency} ${formatInteger(product.denomination)}` : '-',
    'denomination',
    true,
  );
  pushAtGlanceRow('Initial fixing', formatDate(product.initialFixing), undefined, true);
  pushAtGlanceRow('Issue date', formatDate(product.issueDate), undefined, true);
  pushAtGlanceRow('Final fixing', formatDate(product.finalFixing), undefined, true);
  pushAtGlanceRow('Redemption date', formatDate(product.redemptionDate), undefined, true);
  pushAtGlanceRow('Coupon barrier', formatPct(product.couponBarrier), 'couponBarrier', false);
  pushAtGlanceRow('Coupon memory', product.couponMemory ? 'Yes' : 'No', 'couponMemory', false);
  pushAtGlanceRow('Upside', formatPct(product.upside), 'upside', false);
  pushAtGlanceRow('Upside participation', formatPct(product.upsideParticipation), 'upsideParticipation', false);
  pushAtGlanceRow('Downside', formatPct(product.downside), 'downside', false);
  pushAtGlanceRow('Downside participation', formatPct(product.downsideParticipation), 'downsideParticipation', false);
  pushAtGlanceRow('Bonus level', formatPctTrimInteger(product.bonusLevel), 'bonusLevel', false);
  pushAtGlanceRow('Settlement type', product.settlementTypeName || '-', 'settlementType', false);
  pushAtGlanceRow('Grade', product.grade || '-', 'grade', false);
  pushAtGlanceRow('Recovery', product.recovery || '-', 'recovery', false);
  pushAtGlanceRow('Premium', formatPct(product.premiumPct), 'premium', false);
  pushAtGlanceRow('Quotation type', product.quotationType || '-', 'quotationType', false);
  pushAtGlanceRow('Strike', formatPct(product.strikeLevel), 'strike', false);
  pushAtGlanceRow('Basket option type', product.basketOptionTypeName || '-', 'basketOptionType', false);
  pushAtGlanceRow('Upper strike', formatPct(product.upperStrikePct), 'upperStrike', false);
  pushAtGlanceRow('Barrier type', product.barrierStyleName || '-', 'barrierType', false);
  pushAtGlanceRow('Lower barrier', formatPct(product.barrierLevel), 'lowerBarrier', false);
  pushAtGlanceRow('Upper barrier', formatPct(product.barrierUpper), 'upperBarrier', false);
  pushAtGlanceRow('Leverage', formatNumber(product.leverage), 'leverage', false);
  pushAtGlanceRow('Ratio', formatNumber(product.ratio), 'ratio', false);
  pushAtGlanceRow('Stop loss', formatPct(product.stopLoss), 'stopLoss', false);
  pushAtGlanceRow('Coupon trigger', formatPct(product.couponTrigger), 'couponTrigger', false);
  pushAtGlanceRow('Coupon frequency', product.couponFrequency || '-', 'couponFrequency', false);
  pushAtGlanceRow('Coupon type', product.couponType || '-', 'couponType', false);
  pushAtGlanceRow('Capital protection', formatPct(product.capitalProtectionPct), 'capitalProtectionPct', false);
  pushAtGlanceRow('Step up', formatPct(product.couponStepUp), 'stepUp', false);
  pushAtGlanceRow('Coupon minimum', formatPct(product.couponMinPct), 'couponMinimum', false);
  pushAtGlanceRow('Coupon maximum', formatPct(product.couponMaxPct), 'couponMaximum', false);
  pushAtGlanceRow('Traded notional', formatInteger(product.tradedNotional), 'tradedNotional', true);
  pushAtGlanceRow('Current notional', formatInteger(product.currentNotional), 'currentNotional', true);

  const couponDetailsRows = [
    ['Coupon', product.coupon != null ? `${formatNumber(product.coupon)}%` : '-'],
    ['Coupon frequency', product.couponFrequency || '-'],
    ['Coupon type', product.couponType || 'None'],
    ['Reference rate', product.referenceRate || 'None'],
    ['Coupon Min %', product.couponMinPct == null || product.couponMinPct === 0 ? 'None' : `${formatNumber(product.couponMinPct)}%`],
    ['Coupon Max %', product.couponMaxPct == null || product.couponMaxPct === 0 ? 'None' : `${formatNumber(product.couponMaxPct)}%`],
  ].filter(([label]) => {
    const props = sectionProps?.couponDetails || {};
    if (label === 'Reference rate') {
      return props.referenceRate !== false;
    }
    if (label === 'Coupon Min %') {
      return props.couponMinPct !== false;
    }
    if (label === 'Coupon Max %') {
      return props.couponMaxPct !== false;
    }
    return true;
  });

  const earlyRedemptionProps = sectionProps?.earlyRedemption || {};
  const barrierProps = sectionProps?.barrier || {};
  const underlyingsProps = sectionProps?.underlyings || {};
  const couponsProps = sectionProps?.coupons || {};
  const showObservationDate = earlyRedemptionProps.observationDate !== false;
  const showRedemptionLevel = earlyRedemptionProps.redemptionLevel === true;
  const showIssuerCallable = earlyRedemptionProps.showIssuerCallable === true;
  const showExDate = couponsProps.showExDate !== false;
  const showUnderlyingsInChart = sectionProps?.performance?.showUnderlyingsInChart !== false;

  const worstOffBarrierUnderlying = product.underlyings.length
    ? product.underlyings.reduce((worst, current) =>
        current.barrierDistance < worst.barrierDistance ? current : worst,
      )
    : null;
  const worstOffStrikeUnderlying = product.underlyings.length
    ? product.underlyings.reduce((worst, current) =>
        current.strikeDistance < worst.strikeDistance ? current : worst,
      )
    : null;
  const barrierHitIsYes = isAmericanBarrierHit(
    product.barrierStyleName,
    product.barrierHitRecorded,
    worstOffBarrierUnderlying?.barrierDistance ?? null,
  );

  const shouldShowBarrierProp = (propName: string): boolean => barrierProps[propName] !== false;
  const shouldShowUnderlyingsProp = (propName: string): boolean => underlyingsProps[propName] !== false;
  const showUpperStrike = underlyingsProps.upperStrike === true;
  const showAutocallDistance = underlyingsProps.autocallDistance === true;
  const showDistanceCouponBarrier = underlyingsProps.distanceCouponBarrier === true;

  const nextCoupon = product.coupons.find((coupon) => !coupon.isPaid);

  const wrapPage = (inner: string): string => `<section class="page">\n${inner}\n</section>`;

  const fragHeaderGrid = `
      <div class="brand-header">
        <div>
          <p class="eyebrow">${escapeHtml(eyebrowText)}</p>
          <h1>${escapeHtml(product.name)}</h1>
        </div>
        <div class="brand-right">
          <div class="badge">${escapeHtml(product.currency)}</div>
        </div>
      </div>
      <div class="grid">
        <div class="item"><div class="pdf-label">ISIN</div><strong>${escapeHtml(product.isin)}</strong></div>
        <div class="item"><div class="pdf-label">Valor</div><strong>${escapeHtml(product.valor || '-')}</strong></div>
        <div class="item"><div class="pdf-label">Bid</div><strong>${formatNumber(product.bid)}</strong></div>
        <div class="item"><div class="pdf-label">Ask</div><strong>${formatNumber(product.ask)}</strong></div>
      </div>
  `;

  const fragAtGlance =
    sections.atGlance && atGlanceRows.length > 0
      ? `<div class="section-block">
             <h2>At a glance</h2>
             <div class="info-cards-grid">
              ${atGlanceRows
                .map(
                  ([label, value, valueClass]) =>
                    `<div class="info-card"><div class="pdf-label">${escapeHtml(label)}</div><strong class="${valueClass || ''}">${escapeHtml(value)}</strong></div>`,
                )
                .join('')}
             </div>
             </div>`
      : '';

  const fragBarrier = sections.barrier
    ? `<div class="section-block">
             <h2>${escapeHtml(sectionTitles?.barrier || 'Barrier')}</h2>
             <div class="info-cards-grid">
               ${shouldShowBarrierProp('barrierType') ? `<div class="info-card"><div class="pdf-label">Barrier type</div><strong>${escapeHtml(product.barrierStyleName || 'No barrier')}</strong></div>` : ''}
               ${shouldShowBarrierProp('strikeLevel') ? `<div class="info-card"><div class="pdf-label">Strike level</div><strong>${formatPct(product.strikeLevel, 0)}</strong></div>` : ''}
               ${
                 shouldShowBarrierProp('strikeDistance')
                   ? `<div class="info-card"><div class="pdf-label">Strike distance (worst-of)</div><strong class="${negativeClass(
                       worstOffStrikeUnderlying?.strikeDistance,
                     )}">${formatRatioPct(worstOffStrikeUnderlying?.strikeDistance)}</strong></div>`
                   : ''
               }
               ${shouldShowBarrierProp('barrierLevel') ? `<div class="info-card"><div class="pdf-label">Barrier</div><strong>${formatPct(product.barrierLevel, 0)}</strong></div>` : ''}
               ${
                 shouldShowBarrierProp('barrierDistance')
                   ? `<div class="info-card"><div class="pdf-label">Distance From Barrier (worst-of)</div><strong class="${negativeClass(
                       worstOffBarrierUnderlying?.barrierDistance,
                     )}">${formatPct(worstOffBarrierUnderlying?.barrierDistance)}</strong></div>`
                   : ''
               }
               ${shouldShowBarrierProp('barrierHit') ? `<div class="info-card"><div class="pdf-label">Barrier hit</div><strong>${barrierHitIsYes ? 'YES' : 'NO'}</strong></div>` : ''}
               ${shouldShowBarrierProp('couponBarrier') ? `<div class="info-card"><div class="pdf-label">Coupon Barrier</div><strong>${formatPct(product.couponBarrier)}</strong></div>` : ''}
               ${barrierProps.couponMemory ? `<div class="info-card"><div class="pdf-label">Coupon Memory</div><strong>${product.couponMemory ? 'YES' : 'NO'}</strong></div>` : ''}
               ${
                 barrierProps.distanceCouponBarrier && worstOffBarrierUnderlying?.couponBarrierDistance != null
                   ? `<div class="info-card"><div class="pdf-label">Coupon barrier distance (worst-of)</div><strong class="${negativeClass(
                       worstOffBarrierUnderlying.couponBarrierDistance,
                     )}">${formatPct(worstOffBarrierUnderlying.couponBarrierDistance)}</strong></div>`
                   : ''
               }
               ${
                 shouldShowBarrierProp('stopLoss') && product.stopLoss != null
                   ? `<div class="info-card"><div class="pdf-label">Stop Loss</div><strong>${formatNumber(product.stopLoss)}</strong></div>`
                   : ''
               }
               ${
                 shouldShowBarrierProp('distanceStopLoss') && product.stopLossDistance != null
                   ? `<div class="info-card"><div class="pdf-label">Distance Stop Loss</div><strong class="${negativeClass(product.stopLossDistance)}">${formatPct(product.stopLossDistance)}</strong></div>`
                   : ''
               }
             </div>
             </div>`
    : '';

  const fragUnderlyings = sections.underlyings
    ? `<div class="section-block">
             <h2>Underlyings</h2>
             <div class="underlyings-cards">
               ${product.underlyings
                 .map(
                   (underlying) => `
                  <div class="underlying-card">
                    <div class="underlying-card-title">${escapeHtml(underlying.name)}</div>
                    <div class="underlying-grid">
                      <div class="field"><div class="pdf-label">Market price</div><strong>${underlying.marketPrice ? `${escapeHtml(underlying.underlyingCurrency || product.currency)} ${formatNumber(underlying.marketPrice)}` : '-'}</strong></div>
                      <div class="field"><div class="pdf-label">Performance</div><strong class="${underlying.performance >= 0 ? 'value-positive' : 'value-negative'}">${formatPct(underlying.performance)}</strong></div>
                      <div class="field"><div class="pdf-label">Initial fixing level</div><strong>${underlying.initialFixingLevel ? `${escapeHtml(underlying.underlyingCurrency || product.currency)} ${formatNumber(underlying.initialFixingLevel)}` : '-'}</strong></div>
                      ${
                        shouldShowUnderlyingsProp('strikeLevel')
                          ? `<div class="field"><div class="pdf-label">Strike level</div><strong>${underlying.strikeLevel ? `${escapeHtml(underlying.underlyingCurrency || product.currency)} ${formatNumber(underlying.strikeLevel)}` : '-'}</strong></div>`
                          : ''
                      }
                      ${
                        shouldShowUnderlyingsProp('strikeDistance')
                          ? `<div class="field"><div class="pdf-label">Strike distance</div><strong class="${negativeClass(underlying.strikeDistance)}">${formatRatioPct(underlying.strikeDistance)}</strong></div>`
                          : ''
                      }
                      ${showUpperStrike ? `<div class="field"><div class="pdf-label">Upper strike</div><strong>${formatPct(underlying.upperStrikePct)}</strong></div>` : ''}
                      ${
                        shouldShowUnderlyingsProp('barrierLevel')
                          ? `<div class="field"><div class="pdf-label">Barrier level</div><strong>${underlying.barrierLevel ? `${escapeHtml(underlying.underlyingCurrency || product.currency)} ${formatNumber(underlying.barrierLevel)}` : '-'}</strong></div>`
                          : ''
                      }
                      ${
                        shouldShowUnderlyingsProp('barrierDistance')
                          ? `<div class="field"><div class="pdf-label">Barrier distance</div><strong class="${negativeClass(underlying.barrierDistance)}">${formatPct(underlying.barrierDistance)}</strong></div>`
                          : ''
                      }
                      ${
                        showAutocallDistance
                          ? `<div class="field"><div class="pdf-label">Distance from autocall</div><strong class="${negativeClass(underlying.autocallDistance)}">${underlying.autocallDistance != null ? formatRatioPct(underlying.autocallDistance) : '-'}</strong></div>`
                          : ''
                      }
                      ${
                        showDistanceCouponBarrier
                          ? `<div class="field"><div class="pdf-label">Coupon barrier distance</div><strong class="${negativeClass(underlying.couponBarrierDistance)}">${underlying.couponBarrierDistance != null ? formatPct(underlying.couponBarrierDistance) : '-'}</strong></div>`
                          : ''
                      }
                    </div>
                  </div>`,
                 )
                 .join('')}
             </div>
             </div>`
    : '';

  const fragPerformance = sections.performance
    ? `<div class="section-block">
             <h2>Performance</h2>
             ${buildCombinedPerformanceSvg(
               priceHistory,
               underlyingsHistory,
               showUnderlyingsInChart,
               product.underlyings || [],
             )}
             </div>`
    : '';

  const fragCoupons = sections.coupons
    ? `<div class="section-block">
             <h2>Coupons</h2>
             ${
               nextCoupon
                 ? `<p class="next-coupon"><strong>Next coupon:</strong> ${formatDate(nextCoupon.paymentDate)}</p>`
                 : ''
             }
             <table class="coupons-table">
              <thead><tr><th>#</th>${showExDate ? '<th>Ex date</th>' : ''}<th>Payment date</th><th>Amount</th></tr></thead>
              <tbody>
               ${product.coupons
                 .map(
                   (coupon) => `
                    <tr>
                      <td>${coupon.periodNumber}</td>
                      ${showExDate ? `<td>${formatDate(coupon.exDate)}</td>` : ''}
                      <td>${formatDate(coupon.paymentDate)}</td>
                      <td>${coupon.amount ? `${escapeHtml(coupon.amount)}%` : '-'}</td>
                    </tr>`,
                 )
                 .join('')}
              </tbody>
             </table>
             </div>`
    : '';

  const fragEarlyRedemption =
    sections.earlyRedemption && earlyRedemption.length > 0
      ? `<div class="section-block">
             <h2>Early redemption</h2>
             <div class="list-cards">
                ${earlyRedemption
                  .map(
                    (item) => `
                      <div class="list-card">
                        <div class="info-cards-grid compact">
                          ${showObservationDate ? `<div class="info-card"><div class="pdf-label">Observation date</div><strong>${formatDate(item.observationDate)}</strong></div>` : ''}
                          ${
                            showRedemptionLevel || showIssuerCallable
                              ? `<div class="info-card"><div class="pdf-label">Early redemption level</div><strong>${showIssuerCallable ? 'Issuer Callable' : formatPct(item.redemptionLevel)}</strong></div>`
                              : ''
                          }
                        </div>
                      </div>`,
                  )
                  .join('')}
             </div>
             </div>`
      : '';

  const fragCouponDetails =
    sections.couponDetails && couponDetailsRows.length > 0
      ? `<div class="section-block">
             <h2>${escapeHtml(sectionTitles?.couponDetails || 'Coupon details')}</h2>
             <div class="info-cards-grid">
                 ${couponDetailsRows
                   .map(([label, value]) => `<div class="info-card"><div class="pdf-label">${escapeHtml(label)}</div><strong>${escapeHtml(value)}</strong></div>`)
                   .join('')}
             </div>
             </div>`
      : '';

  const fragDisclaimer = `<div class="section-block disclaimer-block">
        <h2>Disclaimer</h2>
        <p class="disclaimer-text"><strong>Assumptions made herein are based on data and models we consider reliable and accurate. This notwithstanding the Issuer and Calebo Capital AG make no representations or warranties with respect to completeness or correctness of the assumptions made herein.</strong></p>
        <p class="disclaimer-text"><strong>Information purposes only: This publication constitutes an advertisement and is intended for professional clients and eligible counterparties only and not for retail clients. It serves only for information purposes and is not research. It may be subject to change at any time without previous notice.</strong></p>
        <p class="disclaimer-text"><strong>No Prospectus: This Publication is not a legally binding document and does not constitute (i) a prospectus or a simplified prospectus pursuant to art. 5 Collective Investment Schemes Act ("CISA"), as such article was in effect immediately prior to the entry into effect of the Swiss Financial Services Act ("FinSA"), (ii) nor a prospectus pursuant to article 40 of the FinSA, (iii) a listing prospectus pursuant to art. 1156 of the Swiss Code of Obligations, (iv) a prospectus within the meaning of the Regulation (EU) 2017/1129 (all as amended and implemented from time to time) or (iv) a prospectus under any other laws and regulations.</strong></p>
        <p class="disclaimer-text"><strong>No Recommendation: This Publication does not constitute, and should not be construed as, an offer to sell, a solicitation to buy, investment advice or a recommendation to buy, sell or otherwise transact in any products. Any prices indicated in this Publication do not represent firm quotes as to size or price.</strong></p>
      </div>`;

  const bodyPagesInner: string =
    pdfOptions?.webRaster
      ? [
          wrapPage(fragHeaderGrid),
          ...(fragAtGlance ? [wrapPage(fragAtGlance)] : []),
          ...(fragBarrier ? [wrapPage(fragBarrier)] : []),
          ...(fragUnderlyings ? [wrapPage(fragUnderlyings)] : []),
          ...(fragPerformance ? [wrapPage(fragPerformance)] : []),
          ...(fragCoupons ? [wrapPage(fragCoupons)] : []),
          ...(fragEarlyRedemption ? [wrapPage(fragEarlyRedemption)] : []),
          ...(fragCouponDetails ? [wrapPage(fragCouponDetails)] : []),
          wrapPage(fragDisclaimer),
        ].join('\n')
      : `${wrapPage(fragHeaderGrid + fragAtGlance + fragBarrier)}${wrapPage(fragUnderlyings + fragPerformance)}${wrapPage(
          fragCoupons + fragEarlyRedemption + fragCouponDetails + fragDisclaimer,
        )}`;

  const webRasterCss = pdfOptions?.webRaster
    ? `
        <style>
          html,
          body {
            width: 794px !important;
            max-width: 794px !important;
            margin: 0 !important;
            background: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body.factsheet-web-raster {
            padding: 10px 12px 12px !important;
            box-sizing: border-box !important;
            font-family: Arial, Helvetica, sans-serif !important;
          }
          body.factsheet-web-raster * {
            box-sizing: border-box !important;
            font-family: Arial, Helvetica, sans-serif !important;
          }
          body.factsheet-web-raster .page {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body.factsheet-web-raster .brand-header {
            display: table !important;
            table-layout: fixed !important;
            width: 100% !important;
            overflow: visible !important;
            margin-bottom: 0 !important;
            padding-bottom: 6px !important;
          }
          body.factsheet-web-raster .brand-header > div:first-child {
            float: none !important;
            display: table-cell !important;
            width: auto !important;
            vertical-align: top !important;
            padding-right: 12px !important;
          }
          body.factsheet-web-raster .brand-right {
            float: none !important;
            display: table-cell !important;
            width: 132px !important;
            vertical-align: top !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          body.factsheet-web-raster .brand-header + .grid {
            clear: none !important;
            margin-top: 6px !important;
          }
          body.factsheet-web-raster .eyebrow {
            margin: 0 0 4px !important;
          }
          body.factsheet-web-raster h1 {
            font-size: 18px !important;
            font-weight: 700 !important;
            color: #111827 !important;
            line-height: 1.2 !important;
          }
          body.factsheet-web-raster h2 {
            font-size: 14px !important;
            font-weight: 700 !important;
            color: #1d4ed8 !important;
            margin: 0 0 6px !important;
          }
          body.factsheet-web-raster th {
            font-size: 11px !important;
            font-weight: 700 !important;
            color: #111827 !important;
          }
          body.factsheet-web-raster .pdf-label,
          body.factsheet-web-raster .eyebrow {
            display: block !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            color: #374151 !important;
            opacity: 1 !important;
            line-height: 1.4 !important;
            min-height: 1.25em !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body.factsheet-web-raster .info-card .pdf-label,
          body.factsheet-web-raster .field .pdf-label {
            font-size: 10px !important;
          }
          body.factsheet-web-raster .item strong,
          body.factsheet-web-raster .info-card strong,
          body.factsheet-web-raster .field strong {
            color: #111827 !important;
            font-weight: 700 !important;
          }
          body.factsheet-web-raster strong.value-negative {
            color: #b91c1c !important;
            font-weight: 700 !important;
          }
          body.factsheet-web-raster strong.value-positive {
            color: #047857 !important;
            font-weight: 700 !important;
          }
          body.factsheet-web-raster .grid {
            margin-bottom: 8px !important;
          }
          body.factsheet-web-raster .grid,
          body.factsheet-web-raster .underlyings-cards,
          body.factsheet-web-raster .info-cards-grid,
          body.factsheet-web-raster .underlying-grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
            width: 100% !important;
            overflow: visible !important;
          }
          body.factsheet-web-raster .underlying-grid {
            gap: 6px 10px !important;
          }
          body.factsheet-web-raster .grid > .item,
          body.factsheet-web-raster .underlyings-cards > .underlying-card {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            box-sizing: border-box !important;
          }
          body.factsheet-web-raster .info-cards-grid > .info-card {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
          }
          body.factsheet-web-raster .underlying-grid > .field {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
          }
          body.factsheet-web-raster .section-block {
            page-break-inside: auto !important;
            break-inside: auto !important;
            position: relative !important;
            margin-bottom: 6px !important;
            padding: 8px 10px !important;
          }
          body.factsheet-web-raster .disclaimer-block {
            margin-top: 4px !important;
          }
          body.factsheet-web-raster .disclaimer-text {
            margin: 0 0 5px !important;
          }
          body.factsheet-web-raster .perf-card {
            padding: 8px !important;
          }
          body.factsheet-web-raster .underlying-card,
          body.factsheet-web-raster .perf-card,
          body.factsheet-web-raster table,
          body.factsheet-web-raster tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          body.factsheet-web-raster .badge {
            display: inline-block !important;
          }
          body.factsheet-web-raster .perf-chart {
            width: 100% !important;
            height: auto !important;
            max-height: none !important;
          }
          body.factsheet-web-raster .perf-chart svg {
            display: block !important;
            width: 100% !important;
            height: auto !important;
            max-width: 100% !important;
          }
          body.factsheet-web-raster .perf-legend {
            display: block !important;
          }
          body.factsheet-web-raster .perf-legend-item {
            display: inline-block !important;
            vertical-align: middle !important;
            margin-right: 12px !important;
            margin-bottom: 8px !important;
          }
          body.factsheet-web-raster .list-cards {
            display: block !important;
          }
          body.factsheet-web-raster .list-cards > .list-card {
            margin-bottom: 6px !important;
          }
          body.factsheet-web-raster .next-coupon {
            margin: 0 0 5px !important;
          }
          body.factsheet-web-raster .item {
            padding: 8px !important;
          }
        </style>
      `
    : '';

  return `
    <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta http-equiv="X-UA-Compatible" content="IE=edge" />
        ${
          pdfOptions?.webRaster
            ? ''
            : `<link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />`
        }
        <title>${escapeHtml(documentTitle)}</title>
        ${webRasterCss}
        <style>
          @page { size: A4; margin: 12mm; }
          :root {
            --text-primary: #111827;
            --text-secondary: #6B7280;
            --line: #E5E7EB;
            --line-soft: #F3F4F6;
            --accent: #2563EB;
            --success: #047857;
            --danger: #B91C1C;
            --card: #FFFFFF;
            --surface: #F9FAFB;
          }
          body {
            font-family: 'Montserrat', Arial, sans-serif;
            color: var(--text-primary);
            font-size: 12px;
            margin: 0;
            background: #fff;
          }
          .page { page-break-after: auto; padding: 4px 0; }
          .brand-header {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            border-bottom: 1px solid var(--line);
            padding-bottom: 10px;
            margin-bottom: 12px;
          }
          .brand-right {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .eyebrow {
            margin: 0 0 6px;
            color: var(--text-secondary);
            font-size: 10px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
          }
          h1 {
            font-size: 20px;
            line-height: 1.25;
            margin: 0;
            max-width: 520px;
          }
          .badge {
            font-size: 11px;
            font-weight: 700;
            color: var(--accent);
            background: #DBEAFE;
            border: 1px solid #BFDBFE;
            border-radius: 999px;
            padding: 6px 10px;
          }
          h2 {
            font-size: 15px;
            margin: 0 0 10px;
            color: var(--accent);
            break-after: avoid-page;
            page-break-after: avoid;
          }
          h3 {
            font-size: 12px;
            margin: 10px 0 6px;
            color: var(--text-secondary);
          }
          .grid {
            position: relative;
            z-index: 1;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 14px;
          }
          .item {
            border: 1px solid var(--line);
            background: var(--surface);
            padding: 10px;
            border-radius: 8px;
          }
          .item span {
            display: block;
            color: var(--text-secondary);
            margin-bottom: 4px;
            font-size: 11px;
          }
          .item strong {
            font-size: 15px;
          }
          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; page-break-inside: avoid; break-inside: avoid; }
          tr { page-break-inside: avoid; break-inside: avoid; }
          td, th {
            border: 1px solid var(--line);
            padding: 7px 8px;
            text-align: left;
            vertical-align: top;
          }
          th {
            background: var(--surface);
            color: var(--text-secondary);
            font-weight: 700;
            font-size: 11px;
            text-transform: uppercase;
          }
          tbody tr:nth-child(even) td {
            background: #FCFCFD;
          }
          .underlyings-cards {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }
          .info-cards-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }
          .info-cards-grid.compact {
            gap: 8px;
          }
          .info-card {
            border: 0;
            border-radius: 0;
            padding: 0;
            background: transparent;
            box-shadow: none;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .info-card span {
            display: block;
            font-size: 10px;
            color: var(--text-secondary);
            margin-bottom: 2px;
          }
          .info-card strong {
            font-size: 11px;
            color: var(--text-primary);
          }
          .list-cards {
            display: grid;
            gap: 10px;
          }
          .list-card {
            border: 0;
            border-radius: 0;
            padding: 0;
            background: transparent;
            box-shadow: none;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .list-card-title {
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 4px;
            color: var(--text-primary);
          }
          .underlying-card {
            border: 1px solid var(--line);
            border-radius: 10px;
            padding: 10px;
            background: #fff;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .underlying-card-title {
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 8px;
            color: var(--text-primary);
          }
          .underlying-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6px 10px;
          }
          .field span {
            display: block;
            font-size: 10px;
            color: var(--text-secondary);
            margin-bottom: 2px;
          }
          .field strong {
            font-size: 11px;
            color: var(--text-primary);
          }
          .pdf-label {
            margin: 0;
          }
          .item .pdf-label {
            display: block;
            color: var(--text-secondary);
            margin-bottom: 4px;
            font-size: 11px;
          }
          .info-card .pdf-label {
            display: block;
            font-size: 10px;
            color: var(--text-secondary);
            margin-bottom: 2px;
          }
          .field .pdf-label {
            display: block;
            font-size: 10px;
            color: var(--text-secondary);
            margin-bottom: 2px;
          }
          .section-block {
            position: relative;
            z-index: 1;
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-before: auto;
            border: 1px solid var(--line);
            border-radius: 10px;
            padding: 12px;
            margin-bottom: 12px;
            background: var(--card);
          }
          .value-positive { color: var(--success); font-weight: 700; }
          .value-negative { color: var(--danger); font-weight: 700; }
          p { margin: 0; color: var(--text-secondary); }
          .disclaimer-block {
            margin-top: 8px;
          }
          .disclaimer-text {
            color: var(--text-primary);
            font-size: 10px;
            line-height: 1.45;
            margin: 0 0 8px;
          }
          .next-coupon {
            margin: 0 0 8px;
            color: var(--text-primary);
            font-size: 11px;
          }
          .perf-card {
            border: 1px solid var(--line);
            border-radius: 10px;
            padding: 10px;
            background: #fff;
          }
          .perf-chart {
            width: 100%;
            height: 240px;
          }
          .perf-legend {
            display: flex;
            flex-wrap: wrap;
            gap: 10px 14px;
            margin-top: 8px;
          }
          .perf-legend-item {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            color: var(--text-primary);
            font-weight: 600;
          }
          .perf-legend-marker {
            width: 20px;
            height: 12px;
            min-width: 20px;
            flex: 0 0 20px;
          }
          .no-data-note {
            margin: 0;
            color: var(--text-secondary);
            font-size: 11px;
          }
          .coupons-table th {
            font-weight: 500;
            color: var(--text-secondary);
          }
          .coupons-table td {
            font-weight: 600;
            color: var(--text-primary);
          }
        </style>
      </head>
      <body${pdfOptions?.webRaster ? ' class="factsheet-web-raster"' : ''}>
        ${bodyPagesInner}
      </body>
    </html>
  `;
};

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PDF_MARGIN_MM = 10;

/** Espacio entre bloques apilados en la misma hoja A4 (mm). */
const PDF_SECTION_GAP_MM = 2;

/**
 * Genera y descarga el PDF en el navegador (sin diálogo de impresión).
 * Apila bloques en una misma hoja si caben; solo abre hoja nueva cuando no hay altura útil.
 */
const downloadPdfOnWeb = async (html: string, filenameBase: string): Promise<void> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('La descarga de PDF no está disponible en este entorno.');
  }

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.left = '0';
  iframe.style.top = '0';
  iframe.style.width = '794px';
  iframe.style.minHeight = '200px';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.zIndex = '-1';
  document.body.appendChild(iframe);

  const frameDocument = iframe.contentDocument;
  const frameWindow = iframe.contentWindow;

  if (!frameDocument || !frameWindow) {
    document.body.removeChild(iframe);
    throw new Error('No se pudo preparar el documento para el PDF.');
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  const waitForImages = async (): Promise<void> => {
    const images = Array.from(frameDocument.images);
    if (images.length === 0) return;

    await Promise.all(
      images.map(
        (img) =>
          new Promise<void>((imageResolve) => {
            if (img.complete) {
              imageResolve();
              return;
            }
            img.onload = () => imageResolve();
            img.onerror = () => imageResolve();
          }),
      ),
    );
  };

  await waitForImages();

  const fontsReady = frameDocument.fonts?.ready;
  if (fontsReady) {
    await fontsReady.catch(() => undefined);
  }
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, 400);
  });

  /** Evita alto “fantasma” en iframes off-screen antes de rasterizar */
  const syncIframeToContentHeight = (): void => {
    const scrollH = Math.max(
      frameDocument.body?.scrollHeight ?? 0,
      frameDocument.documentElement?.scrollHeight ?? 0,
    );
    iframe.style.height = `${Math.min(Math.max(scrollH + 48, 400), 20000)}px`;
  };
  syncIframeToContentHeight();
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

  const body = frameDocument.body;
  if (!body) {
    document.body.removeChild(iframe);
    throw new Error('El documento PDF no tiene contenido.');
  }

  let sections = Array.from(body.querySelectorAll('section.page')) as HTMLElement[];
  if (sections.length === 0) {
    sections = [body];
  }

  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true,
  });

  const usableW = A4_WIDTH_MM - PDF_MARGIN_MM * 2;
  const usableH = A4_HEIGHT_MM - PDF_MARGIN_MM * 2;

  try {
    /** Altura ya ocupada en la hoja actual por debajo del margen superior (mm). */
    let usedHeightMm = 0;

    const placePngBlock = (pngDataUrl: string, blockHeightMm: number): void => {
      if (blockHeightMm < 1) return;

      if (blockHeightMm > usableH + 1) {
        throw new Error('No se pudo colocar un bloque que supera el alto útil de la página.');
      }

      const gapMm = usedHeightMm > 0 ? PDF_SECTION_GAP_MM : 0;
      if (usedHeightMm + gapMm + blockHeightMm > usableH + 0.5) {
        pdf.addPage();
        usedHeightMm = 0;
        placePngBlock(pngDataUrl, blockHeightMm);
        return;
      }

      const yPos = PDF_MARGIN_MM + usedHeightMm + gapMm;
      pdf.addImage(pngDataUrl, 'PNG', PDF_MARGIN_MM, yPos, usableW, blockHeightMm);
      usedHeightMm += gapMm + blockHeightMm;
    };

    const rasterizeTallSection = (
      canvas: HTMLCanvasElement,
      scalePxPerMm: number,
      pageSliceHeightPx: number,
    ): void => {
      if (usedHeightMm > 0) {
        pdf.addPage();
        usedHeightMm = 0;
      }

      let offsetY = 0;
      while (offsetY < canvas.height) {
        const remaining = canvas.height - offsetY;
        const sliceHeightPx = Math.max(1, Math.min(pageSliceHeightPx, remaining));
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext('2d');
        if (!ctx) {
          throw new Error('No se pudo preparar el render de una página del PDF.');
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          offsetY,
          canvas.width,
          sliceHeightPx,
          0,
          0,
          canvas.width,
          sliceHeightPx,
        );
        const sliceMm = sliceHeightPx / scalePxPerMm;
        placePngBlock(sliceCanvas.toDataURL('image/png'), sliceMm);
        offsetY += sliceHeightPx;
      }
    };

    const sharedHtml2CanvasOptions = {
      allowTaint: true,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      scale: 2,
      windowWidth: 794,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc: Document) => {
        const root = clonedDoc.body;
        if (!root) return;
        root.querySelectorAll('.pdf-label, .eyebrow').forEach((el) => {
          const node = el as HTMLElement;
          node.style.display = 'block';
          node.style.color = '#374151';
          node.style.fontFamily = 'Arial, Helvetica, sans-serif';
          node.style.fontWeight = '700';
          node.style.lineHeight = '1.4';
        });
        root.querySelectorAll('h1, h2, .underlying-card-title, th').forEach((el) => {
          (el as HTMLElement).style.fontFamily = 'Arial, Helvetica, sans-serif';
        });
        root.querySelectorAll('svg text').forEach((t) => {
          const el = t as SVGElement;
          const cur = el.getAttribute('fill');
          if (!cur || cur === 'currentColor') {
            el.setAttribute('fill', '#4b5563');
          }
        });
        root.querySelectorAll('strong.value-negative').forEach((el) => {
          const node = el as HTMLElement;
          node.style.color = '#b91c1c';
          node.style.fontWeight = '700';
        });
        root.querySelectorAll('strong.value-positive').forEach((el) => {
          const node = el as HTMLElement;
          node.style.color = '#047857';
          node.style.fontWeight = '700';
        });
      },
    };

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      syncIframeToContentHeight();
      void section.offsetHeight;

      const sectionPixelHeight = Math.ceil(
        Math.max(section.scrollHeight, section.offsetHeight, section.clientHeight, 1),
      );

      const canvas = await html2canvas(section, {
        ...sharedHtml2CanvasOptions,
        windowHeight: sectionPixelHeight + 32,
      });

      const scalePxPerMm = canvas.width / usableW;
      const pageSliceHeightPx = Math.floor(usableH * scalePxPerMm);
      const contentHeightMm = canvas.height / scalePxPerMm;

      const fitsWithoutVerticalSplit = contentHeightMm <= usableH + 0.5;
      if (fitsWithoutVerticalSplit) {
        placePngBlock(canvas.toDataURL('image/png'), contentHeightMm);
        continue;
      }

      rasterizeTallSection(canvas, scalePxPerMm, pageSliceHeightPx);
    }

    pdf.save(`${filenameBase}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
};

export const exportProductPdf = async (input: ExportProductPdfInput): Promise<void> => {
  const rawIsin =
    typeof input.product.isin === 'string' && input.product.isin.trim().length > 0
      ? input.product.isin
      : 'unknown-isin';
  const safeIsin = rawIsin.replace(/[^a-zA-Z0-9-_]/g, '_');
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const filename = `${safeIsin}_product_factsheet_${yyyy}-${mm}-${dd}`;
  const html = buildHtml(input, filename, Platform.OS === 'web' ? { webRaster: true } : undefined);

  if (Platform.OS === 'web') {
    await downloadPdfOnWeb(html, filename);
    return;
  }

  const printResult = await Print.printToFileAsync({ html, base64: false });

  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(printResult.uri, {
      dialogTitle: 'Product factsheet',
      mimeType: 'application/pdf',
      UTI: 'com.adobe.pdf',
    });
    return;
  }

  throw new Error(`PDF generated (${filename}.pdf), but sharing is not available on this platform.`);
};
